import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { JobFilter, emptyJobFilter, Option } from './types';
import ClearFiltersLink from '../../components/ClearFiltersLink';
import FlairIcon, { FlairIconName } from '../../../../atomic/atoms/FlairIcon';
import { useObjectLabels } from '../../hooks/useObjectLabels';
import './CareerOpportunitiesFilterTiles.css';

type Props = {
  filter: JobFilter;
  departmentOptions: Option[];
  locationOptions: Option[];
  statusOptions: Option[];
  onChange: (filter: JobFilter) => void;
};

type FilterTileProps = {
  label: string;
  value: string | null;
  options: Option[];
  placeholder: string;
  icon: string;
  onChange: (value: string | null) => void;
  isSearchable?: boolean;
};

const FilterTile: React.FC<FilterTileProps> = ({
  label,
  value,
  options,
  placeholder,
  icon,
  onChange,
  isSearchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTileClick = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue === value ? null : optionValue);
    setIsOpen(false);
  };

  return (
    <div className="filter-tile-clickable" ref={dropdownRef}>
      <button
        className={`filter-tile-button ${value ? 'has-value' : ''}`}
        onClick={handleTileClick}>
        <FlairIcon icon={icon as FlairIconName} size="sm" />
        <span className="filter-tile-text">{displayText}</span>
        <FlairIcon
          icon={isOpen ? 'chevron-up-outline' : 'chevron-forward-outline'}
          size="xs"
          className="filter-tile-arrow"
        />
      </button>

      {isOpen && (
        <div className="filter-dropdown-menu">
          <div className="filter-dropdown-list">
            {options.map((option) => (
              <button
                key={option.value}
                className={`filter-dropdown-option ${
                  value === option.value ? 'selected' : ''
                }`}
                onClick={() => handleOptionClick(option.value)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CareerOpportunitiesFilterTiles: React.FC<Props> = ({
  filter,
  departmentOptions,
  locationOptions,
  statusOptions,
  onChange,
}) => {
  const { t } = useTranslation();
  const objectLabels = useObjectLabels();

  const handleClearFilter = useCallback(() => {
    onChange(emptyJobFilter);
  }, [onChange]);

  const handleFilterChange = useCallback(
    (filterKey: keyof JobFilter, value: string | null) => {
      onChange({
        ...filter,
        [filterKey]: value,
      });
    },
    [filter, onChange],
  );

  const hasAnyFilterOptions =
    departmentOptions.length > 0 ||
    locationOptions.length > 0 ||
    statusOptions.length > 0;

  return (
    <div className="expenses-filter-tiles-enhanced">
      <div className="filter-tiles-container">
        <div className="filter-tiles-wrapper">
          {departmentOptions.length > 0 && (
            <FilterTile
              label={objectLabels.department}
              value={filter.department}
              options={departmentOptions}
              placeholder={t('recruitment.jobsPage.filters.allDepartments', {
                departmentPlural: objectLabels.departmentPlural,
              })}
              icon="business-outline"
              onChange={(value) => handleFilterChange('department', value)}
              isSearchable={true}
            />
          )}
          {locationOptions.length > 0 && (
            <FilterTile
              label={objectLabels.location}
              value={filter.location}
              options={locationOptions}
              placeholder={t('recruitment.jobsPage.filters.allLocations', {
                locationPlural: objectLabels.locationPlural,
              })}
              icon="location-outline"
              onChange={(value) => handleFilterChange('location', value)}
              isSearchable={true}
            />
          )}
          {statusOptions.length > 0 && (
            <FilterTile
              label={t('recruitment.jobPage.evaluations.status')}
              value={filter.status}
              options={statusOptions}
              placeholder={t('recruitment.jobsPage.filters.allStatuses')}
              icon="checkmark-outline"
              onChange={(value) => handleFilterChange('status', value)}
            />
          )}
          {hasAnyFilterOptions && (
            <div className="clear-filters-tile">
              <ClearFiltersLink onClick={handleClearFilter} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
